import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { LessonStartupPerfOverride } from "@/features/tutor/utils/lessonStartupPerf";
import type { LessonCompleteOverride } from "@/features/tutor/utils/lessonCompletion";

/** Forces a slide's tool on or off; "default" leaves the slide metadata in charge. */
export const AVAILABILITY_OVERRIDES = [
  "default",
  "enabled",
  "disabled",
] as const;
export type AvailabilityOverride = (typeof AVAILABILITY_OVERRIDES)[number];

/**
 * Input:  what the slide metadata says, and the override
 * Output: whether the tool is available
 *
 * Callers apply this only inside `if (__DEV_OVERLAY__)`: the store is
 * persisted and loads in every build, so a stale value must never reach a
 * prod lesson.
 */
export const applyAvailabilityOverride = (
  isSlideEnabled: boolean,
  override: AvailabilityOverride,
): boolean =>
  override === "enabled"
    ? true
    : override === "disabled"
      ? false
      : isSlideEnabled;

interface DebugState {
  debugMenuOpen: boolean;
  scratchpadOverride: AvailabilityOverride;
  calculatorOverride: AvailabilityOverride;
  lessonStartupPerfOverride: LessonStartupPerfOverride;
  lessonCompleteOverride: LessonCompleteOverride;

  setDebugMenuOpen: (open: boolean) => void;
  toggleDebugMenu: () => void;
  setScratchpadOverride: (override: AvailabilityOverride) => void;
  setCalculatorOverride: (override: AvailabilityOverride) => void;
  setLessonStartupPerfOverride: (override: LessonStartupPerfOverride) => void;
  setLessonCompleteOverride: (override: LessonCompleteOverride) => void;
}

export const useDebugStore = create<DebugState>()(
  persist(
    (set) => ({
      debugMenuOpen: false,
      scratchpadOverride: "default",
      calculatorOverride: "default",
      lessonStartupPerfOverride: "default",
      lessonCompleteOverride: "default",

      setDebugMenuOpen: (open) => set({ debugMenuOpen: open }),
      toggleDebugMenu: () =>
        set((state) => ({ debugMenuOpen: !state.debugMenuOpen })),
      setScratchpadOverride: (override) =>
        set({ scratchpadOverride: override }),
      setCalculatorOverride: (override) =>
        set({ calculatorOverride: override }),
      setLessonStartupPerfOverride: (override) =>
        set({ lessonStartupPerfOverride: override }),
      setLessonCompleteOverride: (override) =>
        set({ lessonCompleteOverride: override }),
    }),
    {
      name: "debug-settings",
      // Version 1: scratchpadOverride gained a reader. A value clicked in
      // DebugMenu before then did nothing and must not start applying, so it
      // is reset; the other overrides are kept (without a migrate, zustand
      // would drop them all).
      version: 1,
      migrate: (persisted, version) =>
        version < 1
          ? { ...(persisted as object), scratchpadOverride: "default" }
          : persisted,
      partialize: (state) => ({
        scratchpadOverride: state.scratchpadOverride,
        calculatorOverride: state.calculatorOverride,
        lessonStartupPerfOverride: state.lessonStartupPerfOverride,
        lessonCompleteOverride: state.lessonCompleteOverride,
      }),
    },
  ),
);
